"use client"

import { useEffect, useState, useMemo } from "react"
import { motion } from "framer-motion"
import { Plus, Trash2, X } from 'lucide-react'
import Dashboard from "../../components/dashboard/Dashboard" // Assuming this path is correct
import Section from "../../components/ui/OtherSourcesSection" // Assuming this path is correct
import axios from "axios"
import { ToastContainer, toast } from "react-toastify"
import config from "../../config" // Assuming this path is correct
import { Save } from 'lucide-react'
import ValidationErrorSidebar from '../../components/ui/ValidationErrorSidebar'
import { usePageSave } from '../../contexts/PageSaveContext'
import { validateDeductionsVsGrossForProspectiveUpdate, exceedDeductionErrorMessage } from '../../utils/financialGuards'

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

// Updated default structure to match the new payload
const createDefaultOtherSourcesData = () => ({
  incomeOtherSourcesId: null,
  totalIncomeOtherSources: 0,
  deductionUnderSection57iia: 0,
  incomeReliefUnderSection89AOS: 0,
  otherSources: [], // For main income types and custom "Any Other"
  not89aOthersIncome: [], // For notified countries (top-level)
  dividendInc: { // For dividend quarterly breakdown (top-level)
    dividendIncomeId: null,
    dividend: 0,
    upto15Of6: 0,
    upto15Of9: 0,
    up16Of9To15Of12: 0,
    up16Of12To15Of3: 0,
    up16Of3To31Of3: 0,
  },
})

const OtherSources = () => {
  const { markPageAsSaved, markPageAsUnsaved } = usePageSave()
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [grossExpanded, setGrossExpanded] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showCountryModal, setShowCountryModal] = useState(false)
  const [showDividendModal, setShowDividendModal] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [countryQuarterlyData, setCountryQuarterlyData] = useState({
    upto15Of6: 0,
    upto15Of9: 0,
    up16Of9To15Of12: 0,
    up16Of12To15Of3: 0,
    up16Of3To31Of3: 0,
  })
  const [dividendQuarterlyData, setDividendQuarterlyData] = useState({
    upto15Of6: 0,
    upto15Of9: 0,
    up16Of9To15Of12: 0,
    up16Of12To15Of3: 0,
    up16Of3To31Of3: 0,
  })
  const [otherSourcesData, setOtherSourcesData] = useState(createDefaultOtherSourcesData())
  const [customOtherSources, setCustomOtherSources] = useState([{ id: 1, description: "", amount: 0 }])
  const [validationErrors, setValidationErrors] = useState({})

  useEffect(() => {
    const isSidebarOpen = localStorage.getItem("isSidebarOpen") === "true"
    setIsSidebarOpen(isSidebarOpen)
    
    // Check for prefill data first
    const prefillOtherSources = localStorage.getItem("prefill_other_sources");
    if (prefillOtherSources) {
      try {
        const otherSourcesData = JSON.parse(prefillOtherSources);
        console.log('Loading prefill other sources data:', otherSourcesData);
        
        // Update the otherSourcesData state with prefill data
        setOtherSourcesData(prev => {
          const updatedData = { ...prev };
          
          // Update interest from savings bank account (SAV)
          if (otherSourcesData.interestFromSavings) {
            const existingSavingsIndex = updatedData.otherSources?.findIndex(item => item.OthesIncCode === 'SAV');
            if (existingSavingsIndex >= 0) {
              updatedData.otherSources[existingSavingsIndex].amount = otherSourcesData.interestFromSavings;
            } else {
              updatedData.otherSources = updatedData.otherSources || [];
              updatedData.otherSources.push({
                OthesIncCode: 'SAV',
                amount: otherSourcesData.interestFromSavings,
                description: 'Interest from Savings Bank Account'
              });
            }
          }
          
          // Update interest from deposit (IFD)
          if (otherSourcesData.interestFromDeposit) {
            const existingDepositIndex = updatedData.otherSources?.findIndex(item => item.OthesIncCode === 'IFD');
            if (existingDepositIndex >= 0) {
              updatedData.otherSources[existingDepositIndex].amount = otherSourcesData.interestFromDeposit;
            } else {
              updatedData.otherSources = updatedData.otherSources || [];
              updatedData.otherSources.push({
                OthesIncCode: 'IFD',
                amount: otherSourcesData.interestFromDeposit,
                description: 'Interest from Deposit (Bank/Post Office/Cooperative Society)'
              });
            }
          }
          
          // Update dividend data
          if (otherSourcesData.dividend) {
            updatedData.dividendInc = {
              ...updatedData.dividendInc,
              dividend: otherSourcesData.dividend,
              upto15Of6: otherSourcesData.dividend // Set the dividend amount to upto15Of6 field
            };
            
            // Update dividend quarterly data state
            setDividendQuarterlyData(prev => ({
              ...prev,
              upto15Of6: otherSourcesData.dividend
            }));
          }
          
          return updatedData;
        });
        
        toast.success('Other sources data loaded from prefill data');
        console.log('Loaded prefill other sources data from localStorage');
      } catch (error) {
        console.error('Error loading prefill other sources data:', error);
        // If prefill fails, fetch from API
        fetchOtherSourcesData();
      }
    } else {
      console.log('No prefill other sources data found - fetching from API');
      fetchOtherSourcesData();
    }
  }, [])

  // Listen for prefill data updates
  useEffect(() => {
    const handlePrefillDataUpdate = (event) => {
      const { profile } = event.detail;
      if (profile && profile.otherSourcesDetails) {
        const otherSourcesData = profile.otherSourcesDetails;
        console.log('Received prefill other sources data update:', otherSourcesData);
        
        // Update the otherSourcesData state with prefill data
        setOtherSourcesData(prev => {
          const updatedData = { ...prev };
          
          // Update interest from savings bank account (SAV)
          if (otherSourcesData.interestFromSavings) {
            const existingSavingsIndex = updatedData.otherSources?.findIndex(item => item.OthesIncCode === 'SAV');
            if (existingSavingsIndex >= 0) {
              updatedData.otherSources[existingSavingsIndex].amount = otherSourcesData.interestFromSavings;
            } else {
              updatedData.otherSources = updatedData.otherSources || [];
              updatedData.otherSources.push({
                OthesIncCode: 'SAV',
                amount: otherSourcesData.interestFromSavings,
                description: 'Interest from Savings Bank Account'
              });
            }
          }
          
          // Update interest from deposit (IFD)
          if (otherSourcesData.interestFromDeposit) {
            const existingDepositIndex = updatedData.otherSources?.findIndex(item => item.OthesIncCode === 'IFD');
            if (existingDepositIndex >= 0) {
              updatedData.otherSources[existingDepositIndex].amount = otherSourcesData.interestFromDeposit;
            } else {
              updatedData.otherSources = updatedData.otherSources || [];
              updatedData.otherSources.push({
                OthesIncCode: 'IFD',
                amount: otherSourcesData.interestFromDeposit,
                description: 'Interest from Deposit (Bank/Post Office/Cooperative Society)'
              });
            }
          }
          
          // Update dividend data
          if (otherSourcesData.dividend) {
            updatedData.dividendInc = {
              ...updatedData.dividendInc,
              dividend: otherSourcesData.dividend,
              upto15Of6: otherSourcesData.dividend // Set the dividend amount to upto15Of6 field
            };
            
            // Update dividend quarterly data state
            setDividendQuarterlyData(prev => ({
              ...prev,
              upto15Of6: otherSourcesData.dividend
            }));
          }
          
          return updatedData;
        });
        
        toast.success('Other sources data updated from prefill data');
      }
    };

    window.addEventListener('prefillDataUpdated', handlePrefillDataUpdate);
    
    return () => {
      window.removeEventListener('prefillDataUpdated', handlePrefillDataUpdate);
    };
  }, []);

  const fetchOtherSourcesData = async () => {
    try {
      const response = await axios.get(
        `${config.apiUrl}/income/other/${localStorage.getItem("PAN_NO")}/${localStorage.getItem("YEAR")}`,
      )
      const data = response.data
      if (data.otherSourcesIncome) {
        const fetchedData = data.otherSourcesIncome;

        // Update otherSourcesData state with fetched data, ensuring defaults for new top-level fields
        setOtherSourcesData(prev => ({
          ...prev,
          ...fetchedData,
          not89aOthersIncome: fetchedData.not89aOthersIncome || [],
          dividendInc: fetchedData.dividendInc || createDefaultOtherSourcesData().dividendInc,
        }));

        // Extract custom other sources for "Any Other" field
        const anyOtherSources =
          fetchedData.otherSources?.filter((item) => item.OthesIncCode === "ANY_OTHER") || []
        if (anyOtherSources.length > 0) {
          setCustomOtherSources(
            anyOtherSources.map((item, index) => ({
              id: index + 1,
              description: item.description || "",
              amount: item.amount || 0,
              othersIncomeId: item.othersIncomeId,
            })),
          )
        } else {
          // If no "ANY_OTHER" sources, reset to default single entry
          setCustomOtherSources([{ id: 1, description: "", amount: 0 }]);
        }

        // Set dividend quarterly data from the top-level dividendInc
        if (fetchedData.dividendInc) {
          setDividendQuarterlyData({
            upto15Of6: fetchedData.dividendInc.upto15Of6 || 0,
            upto15Of9: fetchedData.dividendInc.upto15Of9 || 0,
            up16Of9To15Of12: fetchedData.dividendInc.up16Of9To15Of12 || 0,
            up16Of12To15Of3: fetchedData.dividendInc.up16Of12To15Of3 || 0,
            up16Of3To31Of3: fetchedData.dividendInc.up16Of3To31Of3 || 0,
          });
        } else {
          setDividendQuarterlyData(createDefaultOtherSourcesData().dividendInc);
        }
      }
    } catch (error) {
      console.log("Error fetching other sources data:", error)
      toast.error("Failed to fetch other sources data.")
    }
  }

  const grossSalaryItems = useMemo(() => {
    const getOtherSourceValue = (code) => {
      const item = otherSourcesData.otherSources?.find((item) => item.OthesIncCode === code)
      return item?.amount || 0
    }

    // Updated to read from the new top-level not89aOthersIncome
    const getNotifiedCountryValue = (countryCode) => {
      const item = otherSourcesData.not89aOthersIncome?.find((item) => item.not89ACountryCode === countryCode)
      return item?.not89AAmount || 0
    }

    // Updated to read from the new top-level dividendInc
    const getDividendQuarterlyValue = (field) => {
      return otherSourcesData.dividendInc?.[field] || 0
    }

    // Calculate total for notified countries
    const usAmount = getNotifiedCountryValue("US")
    const ukAmount = getNotifiedCountryValue("UK")
    const canadaAmount = getNotifiedCountryValue("CA")
    const totalNotifiedCountry = usAmount + ukAmount + canadaAmount

    // Total dividend now comes from the top-level dividendInc object
    const totalDividend = otherSourcesData.dividendInc?.dividend || 0

    // Calculate total custom other sources
    const totalCustomOther = customOtherSources.reduce((sum, item) => sum + Number.parseFloat(item.amount || 0), 0)

    return [
      {
        id: "a",
        label: "A. Interest from Savings Bank Account",
        value: getOtherSourceValue("SAV").toFixed(2),
        code: "SAV",
      },
      {
        id: "b",
        label: "B. Interest from Deposit (Bank/Post Office/Cooperative Society)",
        value: getOtherSourceValue("IFD").toFixed(2),
        code: "IFD",
      },
      {
        id: "c",
        label: "C. Interest from Income Tax Refund",
        value: getOtherSourceValue("TAX").toFixed(2),
        code: "TAX",
      },
      {
        id: "d",
        label: "D. Family pension",
        value: getOtherSourceValue("FAP").toFixed(2),
        code: "FAP",
      },
      {
        id: "e",
        label:
          "E. Interest accrued on contributions to provident fund to the extent taxable as per first proviso to section 10(11)",
        value: getOtherSourceValue("10(11)(iP)").toFixed(2),
        code: "10(11)(iP)",
      },
      {
        id: "f",
        label:
          "F. Interest accrued on contributions to provident fund to the extent taxable as per second proviso to section 10(11)",
        value: getOtherSourceValue("10(11)(iiP)").toFixed(2),
        code: "10(11)(iiP)",
      },
      {
        id: "g",
        label:
          "G. Interest accrued on contributions to provident fund to the extent taxable as per first proviso to section 10(12)",
        value: getOtherSourceValue("10(12)(iP)").toFixed(2),
        code: "10(12)(iP)",
      },
      {
        id: "h",
        label:
          "H. Interest accrued on contributions to provident fund to the extent taxable as per second proviso to section 10(12)",
        value: getOtherSourceValue("10(12)(iiP)").toFixed(2),
        code: "10(12)(iiP)",
      },
      {
        id: "i",
        label: "I. Any Other",
        value: totalCustomOther.toFixed(2),
        code: "OTH",
        isModal: true,
      },
      {
        id: "j",
        label:
          "J. Income from retirement benefit account maintained in a country other than a country notified u/s 89A",
        value: getOtherSourceValue("OTHNOT89A").toFixed(2),
        code: "OTHNOT89A",
      },
      {
        id: "k",
        label: "K. Income from retirement benefit account maintained in a notified country u/s 89A (1 + 2 + 3)",
        value: totalNotifiedCountry.toFixed(2),
        code: "NOT89A",
        submenu: [
          {
            id: "1",
            label: "1. United State of America",
            value: usAmount.toFixed(2),
            countryCode: "US",
            isModal: true,
          },
          {
            id: "2",
            label: "2. United Kingdom of Great Britain and Northern Ireland",
            value: ukAmount.toFixed(2),
            countryCode: "UK",
            isModal: true,
          },
          {
            id: "3",
            label: "3. Canada",
            value: canadaAmount.toFixed(2),
            countryCode: "CA",
            isModal: true,
          },
        ],
      },
      {
        id: "l",
        label: "L. Dividend",
        value: totalDividend.toFixed(2),
        code: "DIV",
        isModal: true,
      },
      {
        id: "m",
        label: "Less : Income claimed for relief from taxation u/s 89A",
        value: otherSourcesData.incomeReliefUnderSection89AOS?.toFixed(2) || "0.00",
        code: "RELIEF_89A", // Not found in new dataset
      },
      {
        id: "n",
        label: "Less: Deduction u/s 57(iia) (In case of family pension only)",
        value: otherSourcesData.deductionUnderSection57iia?.toFixed(2) || "0.00",
        code: "DEDUCTION_57IIA", // Not found in new dataset
      },
    ];

  }, [otherSourcesData, customOtherSources]) // Dependencies are correct [^1][^2]

  const totalGrossSalary = useMemo(() => {
    return grossSalaryItems.reduce((sum, item) => {
      if (item.id === "m" || item.id === "n") return sum // These are deductions
      return sum + Number.parseFloat(item.value || 0)
    }, 0)
  }, [grossSalaryItems])

  useEffect(() => {
    // localStorage.setItem("income_from_other_sources", totalGrossSalary.toFixed(2))
  }, [totalGrossSalary])

  const handleInputChange = (parentId, newValue, isSubmenu = false, id) => {
    if (parentId === "i") {
      // Handle "Any Other" - open modal
      setShowModal(true)
      return
    }
    const item = grossSalaryItems.find((item) => item.id === parentId)
    if (!item) return

    if (isSubmenu) {
      const submenuItem = item.submenu?.find((sub) => sub.id === id)
      // Check if it's a country field (US, UK, CA)
      if (parentId === "k" && submenuItem?.countryCode) {
        setSelectedCountry({
          code: submenuItem.countryCode,
          name: submenuItem.label,
          amount: submenuItem.value,
        })
        // Load existing quarterly data for this country from the new top-level not89aOthersIncome
        const existingData = otherSourcesData.not89aOthersIncome?.find(
          (item) => item.not89ACountryCode === submenuItem.countryCode,
        )
        if (existingData) {
          setCountryQuarterlyData({
            upto15Of6: existingData.upto15Of6 || 0,
            upto15Of9: existingData.upto15Of9 || 0,
            up16Of9To15Of12: existingData.up16Of9To15Of12 || 0,
            up16Of12To15Of3: existingData.up16Of12To15Of3 || 0,
            up16Of3To31Of3: existingData.up16Of3To31Of3 || 0,
          })
        } else {
          setCountryQuarterlyData({
            upto15Of6: 0,
            upto15Of9: 0,
            up16Of9To15Of12: 0,
            up16Of12To15Of3: 0,
            up16Of3To31Of3: 0,
          })
        }
        setShowCountryModal(true)
        return
      }
    } else {
      // Check if it's the main dividend field
      if (parentId === "l") {
        // Load existing dividend quarterly data from the new top-level dividendInc
        if (otherSourcesData.dividendInc) {
          setDividendQuarterlyData({
            upto15Of6: otherSourcesData.dividendInc.upto15Of6 || 0,
            upto15Of9: otherSourcesData.dividendInc.upto15Of9 || 0,
            up16Of9To15Of12: otherSourcesData.dividendInc.up16Of9To15Of12 || 0,
            up16Of12To15Of3: otherSourcesData.dividendInc.up16Of12To15Of3 || 0,
            up16Of3To31Of3: otherSourcesData.dividendInc.up16Of3To31Of3 || 0,
          })
        } else {
          setDividendQuarterlyData({
            upto15Of6: 0,
            upto15Of9: 0,
            up16Of9To15Of12: 0,
            up16Of12To15Of3: 0,
            up16Of3To31Of3: 0,
          })
        }
        setShowDividendModal(true)
        return
      }
      handleMainItemChange(item.code, newValue)
    }
  }

  const handleCountryModalSave = () => {
    if (!selectedCountry) return
    // Calculate total from quarterly data
    const total = Object.values(countryQuarterlyData).reduce((sum, val) => sum + Number.parseFloat(val || 0), 0)

    setOtherSourcesData((prev) => {
      const updatedNotified = [...(prev.not89aOthersIncome || [])] // Use new top-level array
      const existingIndex = updatedNotified.findIndex((item) => item.not89ACountryCode === selectedCountry.code)

      if (existingIndex >= 0) {
        updatedNotified[existingIndex] = {
          ...updatedNotified[existingIndex],
          not89AAmount: total,
          ...countryQuarterlyData,
        }
      } else {
        updatedNotified.push({
          not89AOthersIncomeId: null, // null for new entries
          not89ACountryCode: selectedCountry.code,
          not89AAmount: total,
          ...countryQuarterlyData,
        })
      }
      return {
        ...prev,
        not89aOthersIncome: updatedNotified, // Update new top-level array
      }
    })
    
    // Validate section 89A relief after country amounts change
    const reliefAmount = Number.parseFloat(otherSourcesData.incomeReliefUnderSection89AOS || 0);
    if (reliefAmount > total) {
      setValidationErrors(prev => ({
        ...prev,
        'section89a_relief_limit': `Income claimed for relief from taxation u/s 89A (₹${reliefAmount.toFixed(2)}) cannot exceed the total income from notified countries (₹${total.toFixed(2)})`
      }));
    } else {
      clearValidationError('section89a_relief_limit');
    }
    
    setShowCountryModal(false)
    setSelectedCountry(null)
  }

  const handleDividendModalSave = () => {
    // Calculate total from quarterly data
    const total = Object.values(dividendQuarterlyData).reduce((sum, val) => sum + Number.parseFloat(val || 0), 0)

    setOtherSourcesData((prev) => {
      // Update the top-level dividendInc object
      const updatedDividendInc = {
        ...prev.dividendInc,
        dividend: total,
        ...dividendQuarterlyData,
      };

      // Also, update the 'DIVIDEND' entry in the 'otherSources' array with the total amount
      // This is crucial because grossSalaryItems still reads from otherSources for 'DIVIDEND'
      const updatedOtherSources = [...(prev.otherSources || [])];
      const existingDividendIndex = updatedOtherSources.findIndex((item) => item.OthesIncCode === "DIVIDEND");

      if (existingDividendIndex >= 0) {
        updatedOtherSources[existingDividendIndex] = {
          ...updatedOtherSources[existingDividendIndex],
          amount: total,
          dividendIncDto: null, // Ensure dividendIncDto is removed or set to null
        };
      } else {
        // If DIVIDEND entry doesn't exist in otherSources, add it
        updatedOtherSources.push({
          othersIncomeId: null,
          description: "Dividend",
          amount: total,
          OthesIncCode: "DIVIDEND",
          not89aOthersIncome: [],
          dividendIncDto: null,
        });
      }

      return {
        ...prev,
        otherSources: updatedOtherSources,
        dividendInc: updatedDividendInc, // Update the top-level dividendInc
      };
    });
    setShowDividendModal(false)
  }

  const handleMainItemChange = (code, value) => {
    markPageAsUnsaved('other-sources')
    const numValue = Number.parseFloat(value || 0)
    setOtherSourcesData((prev) => {
      const updatedOtherSources = [...(prev.otherSources || [])]
      const existingIndex = updatedOtherSources.findIndex((item) => item.OthesIncCode === code)

      if (existingIndex >= 0) {
        updatedOtherSources[existingIndex] = {
          ...updatedOtherSources[existingIndex],
          amount: numValue,
        }
      } else {
        // For new entries, push a new object
        updatedOtherSources.push({
          othersIncomeId: null, // null for new entries
          description: getDescriptionForCode(code),
          amount: numValue,
          OthesIncCode: code,
          not89aOthersIncome: [], // Keep this empty as per new structure
          dividendIncDto: null, // Keep this null as per new structure
        })
      }

      // Special handling for DEDUCTION_57IIA and RELIEF_89A as they are direct properties
      let updatedPrev = { ...prev };
      if (code === "DEDUCTION_57IIA") {
        updatedPrev.deductionUnderSection57iia = numValue;
        // Validate deduction u/s 57(iia) when manually changed
        const familyPensionAmount = updatedPrev.otherSources?.find(item => item.OthesIncCode === "FAP")?.amount || 0;
        const maxAllowedDeduction = Math.min(familyPensionAmount / 3, 25000); // Updated to ₹25,000 for new regime
        
        if (numValue > maxAllowedDeduction) {
          setValidationErrors(prev => ({
            ...prev,
            'family_pension_deduction_limit': `Deduction u/s 57(iia) (₹${numValue.toFixed(2)}) cannot exceed 1/3 of family pension (₹${(familyPensionAmount / 3).toFixed(2)}) or ₹25,000, whichever is lower. Maximum allowed: ₹${maxAllowedDeduction.toFixed(2)}`
          }));
        } else {
          clearValidationError('family_pension_deduction_limit');
        }
      } else if (code === "RELIEF_89A") {
        updatedPrev.incomeReliefUnderSection89AOS = numValue;
        // Validate section 89A relief when it changes
        const totalNotifiedCountry = updatedPrev.not89aOthersIncome?.reduce((sum, item) => {
          return sum + Number.parseFloat(item.not89AAmount || 0);
        }, 0) || 0;
        
        if (numValue > totalNotifiedCountry) {
          setValidationErrors(prev => ({
            ...prev,
            'section89a_relief_limit': `Income claimed for relief from taxation u/s 89A (₹${numValue.toFixed(2)}) cannot exceed the total income from notified countries (₹${totalNotifiedCountry.toFixed(2)})`
          }));
        } else {
          clearValidationError('section89a_relief_limit');
        }
      } else if (code === "FAP") {
        // Auto-calculate deduction u/s 57(iia) when family pension changes
        // Deduction = 1/3 of family pension or ₹25,000, whichever is lower (new regime)
        const familyPensionAmount = numValue;
        const calculatedDeduction = Math.min(familyPensionAmount / 3, 25000); // Updated to ₹25,000 for new regime
        updatedPrev.deductionUnderSection57iia = calculatedDeduction;
        // Clear any existing validation errors for family pension deduction
        clearValidationError('family_pension_deduction_limit');
      }

      return {
        ...updatedPrev,
        otherSources: updatedOtherSources,
      }
    })
  }

  // This function is currently only called for submenu items that open modals.
  // If direct input was allowed on submenu items, this would need to be more robust.
  const handleSubmenuChange = (parentId, submenuId, value) => {
    const numValue = Number.parseFloat(value || 0)
    const parentItem = grossSalaryItems.find((item) => item.id === parentId)
    const submenuItem = parentItem?.submenu?.find((sub) => sub.id === submenuId)
    if (!submenuItem) return

    if (parentId === "k") {
      // This logic is primarily for when the modal saves.
      // Direct input on these fields is not expected based on the current UI flow (they open modals).
    }
  }

  const getDescriptionForCode = (code) => {
    const descriptions = {
      SAVINGS_INTEREST: "Interest from Savings Bank Account",
      DEPOSIT_INTEREST: "Interest from Deposit",
      TAX_REFUND_INTEREST: "Interest from Income Tax Refund",
      FAMILY_PENSION: "Family pension",
      PF_INTEREST_10_11_FIRST: "PF Interest 10(11) First Proviso",
      PF_INTEREST_10_11_SECOND: "PF Interest 10(11) Second Proviso",
      PF_INTEREST_10_12_FIRST: "PF Interest 10(12) First Proviso",
      PF_INTEREST_10_12_SECOND: "PF Interest 10(12) Second Proviso",
      NON_NOTIFIED_COUNTRY: "Income from non-notified country",
      DIVIDEND: "Dividend",
      ANY_OTHER: "Any Other",
      RELIEF_89A: "Income claimed for relief from taxation u/s 89A",
      DEDUCTION_57IIA: "Deduction u/s 57(iia) (In case of family pension only)",
    }
    return descriptions[code] || code
  }
  
  // Validation function for section 89A relief and family pension deduction
  const validateSection89ARelief = () => {
    const errors = {};
    
    // Get the total income from notified countries (item K)
    const totalNotifiedCountry = otherSourcesData.not89aOthersIncome?.reduce((sum, item) => {
      return sum + Number.parseFloat(item.not89AAmount || 0);
    }, 0) || 0;
    
    // Get the relief amount claimed
    const reliefAmount = Number.parseFloat(otherSourcesData.incomeReliefUnderSection89AOS || 0);
    
    // Check if relief amount exceeds total notified country income
    if (reliefAmount > totalNotifiedCountry) {
      errors['section89a_relief_limit'] = `Income claimed for relief from taxation u/s 89A (₹${reliefAmount.toFixed(2)}) cannot exceed the total income from notified countries (₹${totalNotifiedCountry.toFixed(2)})`;
    }
    
    // Validate family pension deduction u/s 57(iia)
    const familyPensionAmount = otherSourcesData.otherSources?.find(item => item.OthesIncCode === "FAP")?.amount || 0;
    const currentDeduction = Number.parseFloat(otherSourcesData.deductionUnderSection57iia || 0);
    const maxAllowedDeduction = Math.min(familyPensionAmount / 3, 25000); // Updated to ₹25,000 for new regime
    
    if (currentDeduction > maxAllowedDeduction) {
      errors['family_pension_deduction_limit'] = `Deduction u/s 57(iia) (₹${currentDeduction.toFixed(2)}) cannot exceed 1/3 of family pension (₹${(familyPensionAmount / 3).toFixed(2)}) or ₹25,000, whichever is lower. Maximum allowed: ₹${maxAllowedDeduction.toFixed(2)}`;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Clear validation errors for a specific field
  const clearValidationError = (errorKey) => {
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  };

  const addCustomOtherSource = () => {
    const newId = customOtherSources.length > 0 ? Math.max(...customOtherSources.map((item) => item.id)) + 1 : 1;
    setCustomOtherSources([...customOtherSources, { id: newId, description: "", amount: 0 }])
  }

  const removeCustomOtherSource = async (id, deleteDataObject) => {

    if (deleteDataObject.othersIncomeId) {
      await deleteOtherIncomeSouces(deleteDataObject.othersIncomeId)
    }
    setCustomOtherSources(customOtherSources.filter((item) => item.id !== id))
  }

  const updateCustomOtherSource = (id, field, value) => {
    markPageAsUnsaved('other-sources')
    setCustomOtherSources((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)))
  }

  const constructApiBody = () => {
    // Prepare other sources DTO
    const otherSources = []
    // Add all main items except "Any Other", "RELIEF_89A", "DEDUCTION_57IIA", and "DIVIDEND" (quarterly data is separate)
    grossSalaryItems.forEach((item) => {
      if (item.code && item.code !== "ANY_OTHER" && item.code !== "RELIEF_89A" && item.code !== "DEDUCTION_57IIA" && item.code !== "DIVIDEND") {
        const amount = Number.parseFloat(item.value || 0)
        if (amount > 0) {
          // Find existing item to get the ID
          const existingItem = otherSourcesData.otherSources?.find((existing) => existing.OthesIncCode === item.code)
          otherSources.push({
            othersIncomeId: existingItem?.othersIncomeId || null, // Use existing ID or null for new
            description: getDescriptionForCode(item.code),
            amount: amount,
            OthesIncCode: item.code,
            not89AOthersIncomeDtos: [], // As per new payload, this is an empty array if not used
          })
        }
      }
    })

    // Add the DIVIDEND item to otherSources, but without dividendIncDto nested
    const dividendItemInOtherSources = otherSourcesData.otherSources?.find(item => item.OthesIncCode === "DIVIDEND");
    if (otherSourcesData.dividendInc?.dividend > 0 || dividendItemInOtherSources) {
      otherSources.push({
        othersIncomeId: dividendItemInOtherSources?.othersIncomeId || null,
        description: "Dividend",
        amount: otherSourcesData.dividendInc?.dividend || 0,
        OthesIncCode: "DIVIDEND",
        not89AOthersIncomeDtos: [],
      });
    }

    // Add custom "Any Other" sources with proper ID handling
    customOtherSources.forEach((custom) => {
      if (custom.description && custom.amount > 0) {
        otherSources.push({
          othersIncomeId: custom.othersIncomeId || null, // Use existing ID from custom source
          description: custom.description,
          amount: Number.parseFloat(custom.amount || 0),
          OthesIncCode: "ANY_OTHER",
          not89AOthersIncomeDtos: [],
        })
      }
    })

    // Prepare not89aOthersIncome (new top-level array)
    const not89aOthersIncome = (otherSourcesData.not89aOthersIncome || []).map((item) => ({
      not89AOthersIncomeId: item.not89AOthersIncomeId || null, // Use existing ID or null
      not89ACountryCode: item.not89ACountryCode,
      not89AAmount: item.not89AAmount,
      upto15Of6: item.upto15Of6 || 0,
      upto15Of9: item.upto15Of9 || 0,
      up16Of9To15Of12: item.up16Of9To15Of12 || 0,
      up16Of12To15Of3: item.up16Of12To15Of3 || 0,
      up16Of3To31Of3: item.up16Of3To31Of3 || 0,
    }))

    // Prepare dividendInc (new top-level object)
    const dividendInc = {
      dividendIncomeId: otherSourcesData.dividendInc?.dividendIncomeId !== "string" ? otherSourcesData.dividendInc?.dividendIncomeId : null,
      dividend: otherSourcesData.dividendInc?.dividend || 0,
      upto15Of6: otherSourcesData.dividendInc?.upto15Of6 || 0,
      upto15Of9: otherSourcesData.dividendInc?.upto15Of9 || 0,
      up16Of9To15Of12: otherSourcesData.dividendInc?.up16Of9To15Of12 || 0,
      up16Of12To15Of3: otherSourcesData.dividendInc?.up16Of12To15Of3 || 0,
      up16Of3To31Of3: otherSourcesData.dividendInc?.up16Of3To31Of3 || 0,
    };

    return {
      otherSourcesIncome: {
        incomeOtherSourcesId: otherSourcesData.incomeOtherSourcesId, // Use existing main ID
        totalIncomeOtherSources: totalGrossSalary,
        deductionUnderSection57iia: Number.parseFloat(
          grossSalaryItems.find((item) => item.code === "DEDUCTION_57IIA")?.value || 0,
        ),
        incomeReliefUnderSection89AOS: Number.parseFloat(
          grossSalaryItems.find((item) => item.code === "RELIEF_89A")?.value || 0,
        ),
        otherSources: otherSources,
        not89aOthersIncome: not89aOthersIncome, // Use new top-level array
        dividendInc: dividendInc, // Use new top-level object
      },
    }
  }

  const handleSubmit = async () => {
    // Validate section 89A relief before submission
    if (!validateSection89ARelief()) {
      toast.error("Please fix validation errors before submitting");
      return;
    }
    
    try {
      setIsSubmitting(true)
      const body = constructApiBody()
      console.log("Submitting Other Sources Data:", body)
      // Guard: ensure deductions do not exceed prospective gross after this save
      const totals = calculateNewRegimeOtherSourcesIncome()
      const guard = await validateDeductionsVsGrossForProspectiveUpdate({
        incomeFromOtherSources: totals.totalGrossSalary || 0,
      })
      if (!guard.ok) {
        toast.error(exceedDeductionErrorMessage)
        setIsSubmitting(false)
        return
      }
      const response = await axios.patch(
        `${config.apiUrl}/income/other/${localStorage.getItem("PAN_NO")}/${localStorage.getItem("YEAR")}`,
        body,
      )
      // Update with returned IDs
      if (response.data && response.data.otherSourcesIncome) {
        setOtherSourcesData(response.data.otherSourcesIncome)
        // Re-fetch custom sources and dividend quarterly data based on updated state
        const anyOtherSources =
          response.data.otherSourcesIncome.otherSources?.filter((item) => item.OthesIncCode === "ANY_OTHER") || []
        if (anyOtherSources.length > 0) {
          setCustomOtherSources(
            anyOtherSources.map((item, index) => ({
              id: index + 1,
              description: item.description || "",
              amount: item.amount || 0,
              othersIncomeId: item.othersIncomeId,
            })),
          )
        } else {
          setCustomOtherSources([{ id: 1, description: "", amount: 0 }]);
        }

        if (response.data.otherSourcesIncome.dividendInc) {
          setDividendQuarterlyData({
            upto15Of6: response.data.otherSourcesIncome.dividendInc.upto15Of6 || 0,
            upto15Of9: response.data.otherSourcesIncome.dividendInc.upto15Of9 || 0,
            up16Of9To15Of12: response.data.otherSourcesIncome.dividendInc.up16Of9To15Of12 || 0,
            up16Of12To15Of3: response.data.otherSourcesIncome.dividendInc.up16Of12To15Of3 || 0,
            up16Of3To31Of3: response.data.otherSourcesIncome.dividendInc.up16Of3To31Of3 || 0,
          });
        } else {
          setDividendQuarterlyData(createDefaultOtherSourcesData().dividendInc);
        }
      }
      setIsSubmitting(false)
      const saved = markPageAsSaved('other-sources')
      if (saved) {
        // Clear prefill data for other sources since page is now saved
        localStorage.removeItem("prefill_other_sources");
        toast.success("Other Sources updated successfully!")
      } else {
        toast.error("Please save your profile first before saving other sources details!")
      }
      localStorage.setItem("income_from_other_sources", totalGrossSalary)
      const totalNetIncome = (totalGrossSalary) -
        (Number.parseFloat(grossSalaryItems.find((item) => item.code === "DEDUCTION_57IIA")?.value || 0)) -
        (Number.parseFloat(grossSalaryItems.find((item) => item.code === "RELIEF_89A")?.value || 0))
      await taxComputation(totalNetIncome)
    } catch (error) {
      setIsSubmitting(false)
      toast.error(error.response?.data?.message || "Something went wrong!")
      console.error("Error:", error)
    }
  }

  // Calculate new regime Other Sources income with updated Family Pension deduction
  const calculateNewRegimeOtherSourcesIncome = () => {
    const totalGrossSalary = grossSalaryItems.reduce((sum, item) => {
      if (item.code === "DEDUCTION_57IIA" || item.code === "RELIEF_89A") {
        return sum; // Skip deductions in gross calculation
      }
      return sum + Number.parseFloat(item.value || 0);
    }, 0);

    // For new regime, use updated Family Pension deduction (min of 1/3rd or ₹25,000)
    const familyPensionAmount = otherSourcesData.otherSources?.find(item => item.OthesIncCode === "FAP")?.amount || 0;
    const newRegimeFamilyPensionDeduction = Math.min(familyPensionAmount / 3, 25000);
    
    // Relief remains the same for both regimes
    const reliefAmount = Number.parseFloat(grossSalaryItems.find((item) => item.code === "RELIEF_89A")?.value || 0);
    
    // New regime net income = Gross - New Family Pension Deduction - Relief
    const newRegimeNetIncome = totalGrossSalary - newRegimeFamilyPensionDeduction - reliefAmount;
    
    return {
      totalGrossSalary,
      newRegimeFamilyPensionDeduction,
      reliefAmount,
      newRegimeNetIncome
    };
  };

  const taxComputation = async (totalAmount) => {
    try {
      const pan = localStorage.getItem("PAN_NO");
      const year = localStorage.getItem("YEAR");

      // Calculate new regime values
      const newRegimeCalculation = calculateNewRegimeOtherSourcesIncome();

      // Fetch both regimes in parallel
      const [oldRes, newRes] = await Promise.all([
        axios.get(`${config.apiUrl}/user/tax-computation/${pan}/${year}/OLD`),
        axios.get(`${config.apiUrl}/user/tax-computation/${pan}/${year}/NEW`)
      ]);

      const prepareRegimeData = (data, regime, amount) => {
        return {
          ...data,
          taxComputationId: data?.taxComputationId || null,
          regime,
          incomeFromOtherSources: amount || 0,
        };
      };

      // Prepare payloads inside `taxComputation` key
      const oldPayload = {
        taxComputation: prepareRegimeData(
          oldRes.data?.taxComputation,
          "OLD",
          totalAmount // Old regime uses the original calculation
        )
      };

      const newPayload = {
        taxComputation: prepareRegimeData(
          newRes.data?.taxComputation,
          "NEW",
          newRegimeCalculation.newRegimeNetIncome // New regime uses updated calculation
        )
      };

      // Update both regimes in parallel
      await Promise.all([
        axios.patch(`${config.apiUrl}/user/tax-computation/${pan}/${year}/OLD`, oldPayload),
        axios.patch(`${config.apiUrl}/user/tax-computation/${pan}/${year}/NEW`, newPayload)
      ]);

    } catch (error) {
      console.error("Error in taxComputation:", error);
    }
  };

  const deleteOtherIncomeSouces = async (id) => {
    try {
      const response = await axios.delete(
        `${config.apiUrl}/income/other/${id}`,
      )
      toast.success("Other Sources deleted successfully!")
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete Other Sources.")
      console.error("Error deleting Other Sources:", error)
    }
  }

  // Prepare other sources comparison data
  const otherSourcesComparisonData = useMemo(() => {
    const totalOtherSourcesIncome = totalGrossSalary
    const totalDeductions = grossSalaryItems
      .filter(item => item.id === "m" || item.id === "n")
      .reduce((sum, item) => sum + Number.parseFloat(item.value || 0), 0)
    const netOtherSourcesIncome = totalOtherSourcesIncome - totalDeductions

    // Calculate new regime values
    const newRegimeCalculation = calculateNewRegimeOtherSourcesIncome()

    const formatCurrency = (value) => {
      return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Math.abs(value))
    }

    return {
      totalOtherSourcesIncome,
      totalDeductions,
      netOtherSourcesIncome,
      oldRegimeTaxable: netOtherSourcesIncome, // Old regime uses original calculation
      newRegimeTaxable: newRegimeCalculation.newRegimeNetIncome, // New regime uses updated calculation
      newRegimeFamilyPensionDeduction: newRegimeCalculation.newRegimeFamilyPensionDeduction,
      difference: netOtherSourcesIncome - newRegimeCalculation.newRegimeNetIncome, // Difference between regimes
      formatCurrency,
    }
  }, [totalGrossSalary, grossSalaryItems, otherSourcesData])

  return (
    <Dashboard 
      salaryData={otherSourcesComparisonData}
      showTaxComparison={true}
    >
      <ToastContainer />
      <div className="p-6 pt-0 pl-0">
        <div className="flex justify-between">
          <div></div>
          <div className="mb-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{ borderRadius: "10px" }}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white transition-colors ${isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700"
                }`}
            >
              {isSubmitting ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {isSubmitting ? "Saving..." : "Update Other Sources"}
            </motion.button>
          </div>
        </div>
        <Section
          title="Income From Other Sources"
          amount={formatCurrency(totalGrossSalary)}
          isExpanded={grossExpanded}
          onToggle={() => setGrossExpanded(!grossExpanded)}
          items={grossSalaryItems}
          isMenu={true}
          onInputChange={handleInputChange}
        />
        {/* Summary */}
        <div className="mt-6 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-800 mb-2">Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-sm text-gray-600">Total Income</div>
              <div className="text-xl font-semibold text-green-600">₹{formatCurrency(totalGrossSalary)}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Total Deductions</div>
              <div className="text-xl font-semibold text-red-600">
                ₹
                {formatCurrency(
                  Number.parseFloat(grossSalaryItems.find((item) => item.code === "DEDUCTION_57IIA")?.value || 0) +
                  Number.parseFloat(grossSalaryItems.find((item) => item.code === "RELIEF_89A")?.value || 0),
                )}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Net Income</div>
              <div className="text-xl font-semibold text-blue-600">
                ₹
                {formatCurrency(
                  totalGrossSalary -
                  Number.parseFloat(grossSalaryItems.find((item) => item.code === "DEDUCTION_57IIA")?.value || 0) -
                  Number.parseFloat(grossSalaryItems.find((item) => item.code === "RELIEF_89A")?.value || 0),
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Any Other Sources Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl space-y-4 shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Any Other Income Sources</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              {customOtherSources.map((source, index) => (
                <div key={source.id} className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium text-gray-700">Source {index + 1}</h3>
                    {customOtherSources.length > 1 && (
                      <button
                        onClick={() => removeCustomOtherSource(source.id, source)}
                        className="p-2 text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Description</label>
                      <input
                        type="text"
                        className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        placeholder="Enter description"
                        value={source.description}
                        onChange={(e) => updateCustomOtherSource(source.id, "description", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Amount</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                        <input
                          type="text"
                          className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          placeholder="0.00"
                          value={source.amount}
                          onChange={(e) => {
                            // Only allow numbers (0-9) and decimal point
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                            const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                            updateCustomOtherSource(source.id, "amount", numericValue);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center pt-4">
              <button
                onClick={addCustomOtherSource}
                className="flex items-center gap-2 px-4 py-2 text-cyan-600 border border-cyan-600 rounded-lg hover:bg-cyan-50"
              >
                <Plus className="w-4 h-4" />
                Add Another Source
              </button>
              <div className="flex gap-3">
                <button
                  className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
                  onClick={() => setShowModal(false)}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Country Quarterly Breakdown Modal */}
      {showCountryModal && selectedCountry && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl space-y-4 shadow-lg">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Quarterly Breakdown - {selectedCountry.name}</h2>
              <button onClick={() => setShowCountryModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Upto 15-Jun-2023</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="0.00"
                    value={countryQuarterlyData.upto15Of6}
                    onChange={(e) => {
                      // Only allow numbers (0-9) and decimal point
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      // Prevent multiple decimal points
                      const parts = value.split('.');
                      const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                      const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                      setCountryQuarterlyData((prev) => ({
                        ...prev,
                        upto15Of6: numericValue,
                      }));
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">From 16-Jun-2023 to 15-Sep-2023</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="0.00"
                    value={countryQuarterlyData.upto15Of9}
                    onChange={(e) => {
                      // Only allow numbers (0-9) and decimal point
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      // Prevent multiple decimal points
                      const parts = value.split('.');
                      const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                      const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                      setCountryQuarterlyData((prev) => ({
                        ...prev,
                        upto15Of9: numericValue,
                      }));
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">From 16-Sep-2023 to 15-Dec-2023</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="0.00"
                    value={countryQuarterlyData.up16Of9To15Of12}
                    onChange={(e) => {
                      // Only allow numbers (0-9) and decimal point
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      // Prevent multiple decimal points
                      const parts = value.split('.');
                      const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                      const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                      setCountryQuarterlyData((prev) => ({
                        ...prev,
                        up16Of9To15Of12: numericValue,
                      }));
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">From 16-Dec-2023 to 15-Mar-2024</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="0.00"
                    value={countryQuarterlyData.up16Of12To15Of3}
                    onChange={(e) => {
                      // Only allow numbers (0-9) and decimal point
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      // Prevent multiple decimal points
                      const parts = value.split('.');
                      const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                      const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                      setCountryQuarterlyData((prev) => ({
                        ...prev,
                        up16Of12To15Of3: numericValue,
                      }));
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">From 16-Mar-2024 to 31-Mar-2024</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="0.00"
                    value={countryQuarterlyData.up16Of3To31Of3}
                    onChange={(e) => {
                      // Only allow numbers (0-9) and decimal point
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      // Prevent multiple decimal points
                      const parts = value.split('.');
                      const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                      const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                      setCountryQuarterlyData((prev) => ({
                        ...prev,
                        up16Of3To31Of3: numericValue,
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Total:</span>
                  <span className="font-semibold text-cyan-600">
                    ₹
                    {Object.values(countryQuarterlyData)
                      .reduce((sum, val) => sum + Number.parseFloat(val || 0), 0)
                      .toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                onClick={() => setShowCountryModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
                onClick={handleCountryModalSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Dividend Quarterly Breakdown Modal */}
      {showDividendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl space-y-4 shadow-lg">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Dividend Quarterly Breakdown</h2>
              <button onClick={() => setShowDividendModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Upto 15-Jun-2023</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="0.00"
                    value={dividendQuarterlyData.upto15Of6}
                    onChange={(e) => {
                      // Only allow numbers (0-9) and decimal point
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      // Prevent multiple decimal points
                      const parts = value.split('.');
                      const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                      const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                      setDividendQuarterlyData((prev) => ({
                        ...prev,
                        upto15Of6: numericValue,
                      }));
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">From 16-Jun-2023 to 15-Sep-2023</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="0.00"
                    value={dividendQuarterlyData.upto15Of9}
                  onChange={(e) => {
                    // Only allow numbers (0-9) and decimal point
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    // Prevent multiple decimal points
                    const parts = value.split('.');
                    const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                    const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                    setDividendQuarterlyData((prev) => ({
                      ...prev,
                      upto15Of9: numericValue,
                    }));
                  }}
                />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">From 16-Sep-2023 to 15-Dec-2023</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="0.00"
                    value={dividendQuarterlyData.up16Of9To15Of12}
                  onChange={(e) => {
                    // Only allow numbers (0-9) and decimal point
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    // Prevent multiple decimal points
                    const parts = value.split('.');
                    const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                    const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                    setDividendQuarterlyData((prev) => ({
                      ...prev,
                      up16Of9To15Of12: numericValue,
                    }));
                  }}
                />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">From 16-Dec-2023 to 15-Mar-2024</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="0.00"
                    value={dividendQuarterlyData.up16Of12To15Of3}
                  onChange={(e) => {
                    // Only allow numbers (0-9) and decimal point
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    // Prevent multiple decimal points
                    const parts = value.split('.');
                    const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                    const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                    setDividendQuarterlyData((prev) => ({
                      ...prev,
                      up16Of12To15Of3: numericValue,
                    }));
                  }}
                />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">From 16-Mar-2024 to 31-Mar-2024</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="text"
                    className="w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="0.00"
                    value={dividendQuarterlyData.up16Of3To31Of3}
                  onChange={(e) => {
                    // Only allow numbers (0-9) and decimal point
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    // Prevent multiple decimal points
                    const parts = value.split('.');
                    const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                    const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                    setDividendQuarterlyData((prev) => ({
                      ...prev,
                      up16Of3To31Of3: numericValue,
                    }));
                  }}
                />
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Total:</span>
                  <span className="font-semibold text-cyan-600">
                    ₹
                    {Object.values(dividendQuarterlyData)
                      .reduce((sum, val) => sum + Number.parseFloat(val || 0), 0)
                      .toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                onClick={() => setShowDividendModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
                onClick={handleDividendModalSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Validation Error Sidebar */}
      <ValidationErrorSidebar
        errors={validationErrors}
        isVisible={true}
        onFieldClick={(fieldKey) => {
          // Handle field click to focus on the specific field
          console.log("Field clicked:", fieldKey);
        }}
        title="Other Sources Validation Errors"
      />
    </Dashboard>
  )
}

export default OtherSources
